// src/SuperTaxonomy.js
import React, { useEffect, useMemo, useState } from 'react';
import API from './api';

const emptyNewItem = {
  code: '',
  label: '',
  is_active: 1,
};

const emptyNewCategory = {
  code: '',
  label: '',
  description: '',
  is_active: 1,
};

function SuperTaxonomy() {
  const [categories, setCategories] = useState([]); // taxonomy_categories
  const [items, setItems] = useState([]); // taxonomy rows

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  const [newItem, setNewItem] = useState(emptyNewItem);
  const [savingItemId, setSavingItemId] = useState(null); // id ili -1 (add)

  const [newCategory, setNewCategory] = useState(emptyNewCategory);
  const [savingCategory, setSavingCategory] = useState(false);

  const [message, setMessage] = useState('');
  const [search, setSearch] = useState('');

  // confirm modali
  const [confirmCategoryDelete, setConfirmCategoryDelete] = useState({
    open: false,
    category: null,
  });

  const [confirmItemDelete, setConfirmItemDelete] = useState({
    open: false,
    item: null,
  });

  // ---------- helpers ----------
  function showMessage(msg) {
    setMessage(msg);
    if (msg) {
      setTimeout(() => setMessage(''), 2500);
    }
  }

  const categoryById = useMemo(() => {
    const map = {};
    (categories || []).forEach((c) => {
      map[c.id] = c;
    });
    return map;
  }, [categories]);

  const groupedItemsByCategoryId = useMemo(() => {
    const map = {};
    (items || []).forEach((it) => {
      const cid = it.taxonomy_category_id;
      if (!map[cid]) map[cid] = [];
      map[cid].push(it);
    });
    Object.keys(map).forEach((cid) => {
      map[cid].sort((a, b) => a.code.localeCompare(b.code));
    });
    return map;
  }, [items]);

  const currentCategory = selectedCategoryId
    ? categoryById[selectedCategoryId] || null
    : null;

  const currentCategoryItemsAll = useMemo(() => {
    if (!selectedCategoryId) return [];
    return groupedItemsByCategoryId[selectedCategoryId] || [];
  }, [groupedItemsByCategoryId, selectedCategoryId]);

  const currentCategoryItems = useMemo(() => {
    if (!search.trim()) return currentCategoryItemsAll;
    const q = search.trim().toLowerCase();
    return currentCategoryItemsAll.filter(
      (it) =>
        it.code.toLowerCase().includes(q) ||
        it.label.toLowerCase().includes(q)
    );
  }, [currentCategoryItemsAll, search]);

  // ---------- initial load ----------
  useEffect(() => {
    async function loadAll() {
      try {
        setLoading(true);
        setLoadError('');

        const [catRes, itemsRes] = await Promise.all([
          API.get('/super/taxonomy-categories'),
          API.get('/super/taxonomy'),
        ]);

        const cats = catRes.data || [];
        const its = itemsRes.data || [];

        setCategories(cats);
        setItems(its);

        if (cats.length > 0) {
          setSelectedCategoryId(cats[0].id);
        } else {
          setSelectedCategoryId(null);
        }
      } catch (err) {
        console.error('Error loading taxonomy data', err);
        setLoadError('Failed to load taxonomy data');
      } finally {
        setLoading(false);
      }
    }

    loadAll();
  }, []);

  // ---------- handlers: new category ----------
  const handleNewCategoryChange = (field, value) => {
    setNewCategory((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddCategory = async () => {
    if (!newCategory.code.trim() || !newCategory.label.trim()) {
      showMessage('Category code and label are required');
      return;
    }

    try {
      setSavingCategory(true);
      const payload = {
        code: newCategory.code.trim(),
        label: newCategory.label.trim(),
        description: newCategory.description.trim() || '',
        is_active: newCategory.is_active ? 1 : 0,
      };
      const res = await API.post('/super/taxonomy-categories', payload);
      const created = res.data;

      setCategories((prev) =>
        [...prev, created].sort((a, b) => a.code.localeCompare(b.code))
      );
      setNewCategory(emptyNewCategory);

      // ako nije izabrana kategorija, postavi ovu
      if (!selectedCategoryId) {
        setSelectedCategoryId(created.id);
      }

      showMessage('Taxonomy category added');
    } catch (err) {
      console.error('Error adding taxonomy category', err);
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Failed to add taxonomy category';
      showMessage(msg);
    } finally {
      setSavingCategory(false);
    }
  };

  // ---------- handlers: delete category ----------
  const askDeleteCategory = (category) => {
    setConfirmCategoryDelete({
      open: true,
      category,
    });
  };

  const handleConfirmCategoryDelete = async () => {
    if (!confirmCategoryDelete.open || !confirmCategoryDelete.category) {
      setConfirmCategoryDelete({ open: false, category: null });
      return;
    }

    const cat = confirmCategoryDelete.category;

    try {
      await API.delete(`/super/taxonomy-categories/${cat.id}`);

      setCategories((prev) => prev.filter((c) => c.id !== cat.id));
      setItems((prev) =>
        prev.filter((it) => it.taxonomy_category_id !== cat.id)
      );

      showMessage(
        `Category "${cat.code}" deleted (including all related taxonomy items, student_taxonomy and user_taxonomy rows).`
      );

      if (selectedCategoryId === cat.id) {
        const remaining = categories.filter((c) => c.id !== cat.id);
        if (remaining.length > 0) {
          setSelectedCategoryId(remaining[0].id);
        } else {
          setSelectedCategoryId(null);
        }
      }
    } catch (err) {
      console.error('Error deleting taxonomy category', err);
      showMessage('Failed to delete taxonomy category');
    } finally {
      setConfirmCategoryDelete({ open: false, category: null });
    }
  };

  // ---------- handlers: new item ----------
  const handleNewItemChange = (field, value) => {
    setNewItem((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddItem = async () => {
    if (!selectedCategoryId) {
      showMessage('Select a category first');
      return;
    }
    if (!newItem.code.trim() || !newItem.label.trim()) {
      showMessage('Code and label are required');
      return;
    }

    try {
      setSavingItemId(-1);
      const payload = {
        taxonomy_category_id: selectedCategoryId,
        code: newItem.code.trim(),
        label: newItem.label.trim(),
        is_active: newItem.is_active ? 1 : 0,
      };
      const res = await API.post('/super/taxonomy', payload);
      const created = res.data;

      setItems((prev) => [...prev, created]);
      setNewItem(emptyNewItem);
      showMessage('Taxonomy item added');
    } catch (err) {
      console.error('Error adding taxonomy item', err);
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Failed to add taxonomy item';
      showMessage(msg);
    } finally {
      setSavingItemId(null);
    }
  };

  // ---------- handlers: edit item ----------
  const handleItemFieldChange = (id, field, value) => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === id ? { ...it, [field]: value } : it
      )
    );
  };

  const handleSaveItem = async (item) => {
    try {
      setSavingItemId(item.id);
      const payload = {
        code: item.code,
        label: item.label,
        is_active: item.is_active ? 1 : 0,
        taxonomy_category_id: item.taxonomy_category_id,
      };
      const res = await API.put(`/super/taxonomy/${item.id}`, payload);
      const updated = res.data;

      setItems((prev) =>
        prev.map((it) => (it.id === updated.id ? updated : it))
      );
      showMessage('Taxonomy item saved');
    } catch (err) {
      console.error('Error saving taxonomy item', err);
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Failed to save taxonomy item';
      showMessage(msg);
    } finally {
      setSavingItemId(null);
    }
  };

  // ---------- handlers: delete item ----------
  const askDeleteItem = (item) => {
    setConfirmItemDelete({ open: true, item });
  };

  const handleConfirmItemDelete = async () => {
    if (!confirmItemDelete.open || !confirmItemDelete.item) {
      setConfirmItemDelete({ open: false, item: null });
      return;
    }
    const item = confirmItemDelete.item;

    try {
      await API.delete(`/super/taxonomy/${item.id}`);
      setItems((prev) => prev.filter((it) => it.id !== item.id));
      showMessage('Taxonomy item deleted');
    } catch (err) {
      console.error('Error deleting taxonomy item', err);
      showMessage('Failed to delete taxonomy item');
    } finally {
      setConfirmItemDelete({ open: false, item: null });
    }
  };

  // ---------- render: loading / error ----------
  if (loading) {
    return (
      <div className="p-4">
        <h1 className="text-xl font-semibold mb-2">
          Global Taxonomy (Super Admin)
        </h1>
        <p>Loading…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="p-4">
        <h1 className="text-xl font-semibold mb-2">
          Global Taxonomy (Super Admin)
        </h1>
        <p className="text-red-600 text-sm">{loadError}</p>
      </div>
    );
  }

  const currentCategoryCount = currentCategoryItemsAll.length;

  return (
    <div className="flex h-full relative">
      {/* LEFT COLUMN – categories list + add-new */}
      <div className="w-80 border-r border-gray-200 flex flex-col">
        <div className="p-3 border-b border-gray-200">
          <h2 className="font-semibold text-sm">
            Taxonomy categories
          </h2>
          <p className="text-[11px] text-gray-500 mt-1">
            Global tags for all organizations (domains, populations, approaches…).
          </p>
        </div>

        {/* Add category box */}
        <div className="p-3 border-b border-gray-200 bg-gray-50">
          <div className="text-[11px] font-semibold mb-1">
            Add category
          </div>
          <div className="mb-1">
            <input
              className="border rounded px-2 py-1 text-[11px] w-full mb-1"
              placeholder="code (e.g. domain)"
              value={newCategory.code}
              onChange={(e) =>
                handleNewCategoryChange('code', e.target.value)
              }
            />
            <input
              className="border rounded px-2 py-1 text-[11px] w-full mb-1"
              placeholder="label (e.g. Domains)"
              value={newCategory.label}
              onChange={(e) =>
                handleNewCategoryChange('label', e.target.value)
              }
            />
          </div>
          <textarea
            className="border rounded px-2 py-1 text-[11px] w-full mb-1"
            placeholder="Description (optional)"
            value={newCategory.description}
            onChange={(e) =>
              handleNewCategoryChange('description', e.target.value)
            }
          />
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center text-[11px]">
              <input
                type="checkbox"
                className="mr-1"
                checked={!!newCategory.is_active}
                onChange={(e) =>
                  handleNewCategoryChange(
                    'is_active',
                    e.target.checked ? 1 : 0
                  )
                }
              />
              <span>Active</span>
            </label>
            <button
              type="button"
              className="ml-auto text-[11px] px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
              onClick={handleAddCategory}
              disabled={savingCategory}
            >
              {savingCategory ? 'Adding…' : 'Add'}
            </button>
          </div>
        </div>

        {/* Existing categories */}
        <div className="flex-1 overflow-y-auto">
          {categories.length === 0 && (
            <div className="p-3 text-xs text-gray-500">
              No categories yet. Use “Add category” above to create the first one.
            </div>
          )}

          {categories
            .slice()
            .sort((a, b) => a.code.localeCompare(b.code))
            .map((cat) => {
              const count =
                groupedItemsByCategoryId[cat.id]?.length || 0;
              const selected = selectedCategoryId === cat.id;
              return (
                <div
                  key={cat.id}
                  className={`px-3 py-2 border-b border-gray-100 text-sm cursor-pointer hover:bg-gray-50 ${
                    selected ? 'bg-blue-50' : 'bg-white'
                  }`}
                  onClick={() => setSelectedCategoryId(cat.id)}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">
                      {cat.label}
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-800 text-white">
                      {count}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <div className="text-[11px] text-gray-500 pr-1">
                      <span className="font-mono text-[10px]">
                        {cat.code}
                      </span>{' '}
                      {cat.description && (
                        <span>– {cat.description}</span>
                      )}
                      {!cat.is_active && (
                        <span className="ml-1 text-[10px] px-1 rounded bg-gray-200 text-gray-700">
                          inactive
                        </span>
                      )}
                    </div>
                    {count > 0 && (
                      <button
                        type="button"
                        className="text-[10px] text-red-600 underline ml-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          askDeleteCategory(cat);
                        }}
                      >
                        Delete
                      </button>
                    )}
                    {count === 0 && (
                      <button
                        type="button"
                        className="text-[10px] text-red-600 underline ml-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          askDeleteCategory(cat);
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* RIGHT COLUMN – items for selected category */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h1 className="text-lg font-semibold">
              Global Taxonomy
            </h1>
            <p className="text-xs text-gray-500">
              Configure global tags used across students and providers
              (<code>student_taxonomy</code>, <code>user_taxonomy</code>).
              This taxonomy is not organization-specific.
            </p>
          </div>
          {currentCategory && (
            <div className="text-right">
              <div className="text-xs text-gray-600">
                Active category:
              </div>
              <div className="text-sm font-semibold">
                {currentCategory.label}
              </div>
              <div className="text-[11px] text-gray-500">
                {currentCategoryCount} items
              </div>
            </div>
          )}
        </div>

        {/* Message */}
        {message && (
          <div className="px-4 pt-2">
            <div className="bg-blue-50 text-blue-700 text-xs px-3 py-2 rounded border border-blue-200">
              {message}
            </div>
          </div>
        )}

        <div className="flex-1 flex flex-col p-4 gap-3">
          {/* Add new item */}
          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h2 className="text-sm font-semibold">
                  Add taxonomy item
                </h2>
                <p className="text-[11px] text-gray-500">
                  First select a category on the left, then add a new code/label pair.
                </p>
              </div>
              {currentCategory && (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-600 text-white">
                  {currentCategory.label}
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
              <div>
                <label className="block text-[11px] font-medium mb-1">
                  Code
                </label>
                <input
                  className="border rounded px-2 py-1 text-xs w-full"
                  placeholder="e.g. COGNITIVE"
                  value={newItem.code}
                  onChange={(e) =>
                    handleNewItemChange('code', e.target.value)
                  }
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[11px] font-medium mb-1">
                  Label
                </label>
                <input
                  className="border rounded px-2 py-1 text-xs w-full"
                  placeholder="e.g. Cognitive domain"
                  value={newItem.label}
                  onChange={(e) =>
                    handleNewItemChange('label', e.target.value)
                  }
                />
              </div>
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center text-[11px]">
                  <input
                    type="checkbox"
                    className="mr-1"
                    checked={!!newItem.is_active}
                    onChange={(e) =>
                      handleNewItemChange(
                        'is_active',
                        e.target.checked ? 1 : 0
                      )
                    }
                  />
                  <span>Active</span>
                </label>
                <button
                  type="button"
                  className="ml-auto px-3 py-1.5 rounded text-xs bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                  onClick={handleAddItem}
                  disabled={savingItemId === -1}
                >
                  {savingItemId === -1 ? 'Adding…' : 'Add'}
                </button>
              </div>
            </div>
          </div>

          {/* Filter & list */}
          <div className="flex items-center justify-between mb-1">
            <div className="text-xs text-gray-600">
              {currentCategory ? (
                <>
                  Editing category:{' '}
                  <span className="font-semibold">
                    {currentCategory.label}
                  </span>{' '}
                  (<span className="font-mono">
                    {currentCategory.code}
                  </span>
                  )
                </>
              ) : (
                'Select a category on the left.'
              )}
            </div>
            <div className="flex items-center gap-2">
              <input
                className="border rounded px-2 py-1 text-xs w-40"
                placeholder="Search in this category…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={!selectedCategoryId}
              />
            </div>
          </div>

          <div className="flex-1 border border-gray-200 rounded-lg overflow-hidden">
            <div className="max-h-full overflow-y-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border-b border-gray-200 px-2 py-1 text-left w-32">
                      Code
                    </th>
                    <th className="border-b border-gray-200 px-2 py-1 text-left">
                      Label
                    </th>
                    <th className="border-b border-gray-200 px-2 py-1 text-center w-24">
                      Active
                    </th>
                    <th className="border-b border-gray-200 px-2 py-1 text-center w-32">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!selectedCategoryId ? (
                    <tr>
                      <td
                        colSpan="4"
                        className="px-3 py-3 text-center text-[11px] text-gray-500"
                      >
                        Select a category on the left to view and edit its
                        taxonomy items.
                      </td>
                    </tr>
                  ) : currentCategoryItems.length === 0 ? (
                    <tr>
                      <td
                        colSpan="4"
                        className="px-3 py-3 text-center text-[11px] text-gray-500"
                      >
                        No taxonomy items for this category yet. Add at least
                        one code/label pair above.
                      </td>
                    </tr>
                  ) : (
                    currentCategoryItems.map((it) => (
                      <tr key={it.id} className="hover:bg-gray-50">
                        <td className="border-b border-gray-100 px-2 py-1 align-top">
                          <input
                            className="border rounded px-1 py-0.5 text-[11px] w-full"
                            value={it.code}
                            onChange={(e) =>
                              handleItemFieldChange(
                                it.id,
                                'code',
                                e.target.value
                              )
                            }
                          />
                        </td>
                        <td className="border-b border-gray-100 px-2 py-1 align-top">
                          <input
                            className="border rounded px-1 py-0.5 text-[11px] w-full"
                            value={it.label}
                            onChange={(e) =>
                              handleItemFieldChange(
                                it.id,
                                'label',
                                e.target.value
                              )
                            }
                          />
                        </td>
                        <td className="border-b border-gray-100 px-2 py-1 text-center align-middle">
                          <button
                            type="button"
                            className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[11px] ${
                              it.is_active
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-200 text-gray-600'
                            }`}
                            onClick={() =>
                              handleItemFieldChange(
                                it.id,
                                'is_active',
                                it.is_active ? 0 : 1
                              )
                            }
                          >
                            {it.is_active ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td className="border-b border-gray-100 px-2 py-1 text-center align-middle">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              type="button"
                              className="px-2 py-0.5 rounded text-[11px] bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                              onClick={() => handleSaveItem(it)}
                              disabled={savingItemId === it.id}
                            >
                              {savingItemId === it.id
                                ? 'Saving…'
                                : 'Save'}
                            </button>
                            <button
                              type="button"
                              className="px-2 py-0.5 rounded text-[11px] border border-red-500 text-red-600 hover:bg-red-50"
                              onClick={() => askDeleteItem(it)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="text-[11px] text-gray-500 mt-1">
            Note: Deactivating a taxonomy item keeps it in the system but hides
            it from new assignments. Deleting an item removes it completely and
            also deletes any related rows in <code>student_taxonomy</code> and{' '}
            <code>user_taxonomy</code> (via foreign-key cascade).
          </div>
        </div>
      </div>

      {/* CONFIRM DELETE CATEGORY MODAL */}
      {confirmCategoryDelete.open && confirmCategoryDelete.category && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-4 w-full max-w-md">
            <h3 className="text-sm font-semibold mb-2">
              Delete category &quot;
              {confirmCategoryDelete.category.code}&quot;?
            </h3>
            <p className="text-xs text-gray-600 mb-3">
              Are you sure you want to delete this taxonomy category? This will:
            </p>
            <ul className="list-disc list-inside text-xs text-gray-600 mb-3">
              <li>Delete all taxonomy items in this category.</li>
              <li>
                Delete all related rows in <code>student_taxonomy</code> and{' '}
                <code>user_taxonomy</code> (due to foreign-key cascade).
              </li>
              <li>This action cannot be undone.</li>
            </ul>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="text-xs px-3 py-1 rounded border border-gray-300 hover:bg-gray-50"
                onClick={() =>
                  setConfirmCategoryDelete({ open: false, category: null })
                }
              >
                Cancel
              </button>
              <button
                type="button"
                className="text-xs px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                onClick={handleConfirmCategoryDelete}
              >
                Delete category
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRM DELETE SINGLE ITEM MODAL */}
      {confirmItemDelete.open && confirmItemDelete.item && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-4 w-full max-w-md">
            <h3 className="text-sm font-semibold mb-2">
              Delete taxonomy item &quot;
              {confirmItemDelete.item.code}&quot;?
            </h3>
            <p className="text-xs text-gray-600 mb-3">
              This will delete this taxonomy code/label and any related links in{' '}
              <code>student_taxonomy</code> and <code>user_taxonomy</code>{' '}
              (foreign-key cascade). This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="text-xs px-3 py-1 rounded border border-gray-300 hover:bg-gray-50"
                onClick={() =>
                  setConfirmItemDelete({ open: false, item: null })
                }
              >
                Cancel
              </button>
              <button
                type="button"
                className="text-xs px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                onClick={handleConfirmItemDelete}
              >
                Delete item
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperTaxonomy;

